import { useWrapAsync } from "@paroicms/front-app-log";
import type { ChartData } from "chart.js";
import { useEffect, useState } from "react";
import type { RequestError } from "../../../../../api-requests/statistics";
import { apiRequestWrapper } from "../../../../../api/shared/http-client";
import StatsCard from "../../../../../components/utils/StatsCard/StatsCard";
import { checkIsRequestError } from "../../../../../helpers/check-schema-field-type";
import { generateHexCodeColor } from "../../../../../helpers/generate-random-hex-color";
import { getSeq } from "../../../../../helpers/get-seq";
import ReferenceDataDataTable from "../../../utils/ReferenceDataDataTable/ReferenceDataDataTable";
import ChartPart from "../ChartPart/ChartPart";

import { fetchOneGeneratedReference } from "../../../../../api/application/reference";
import type { GeneratedReference } from "../../../../../api/infrastructure/reference.formatter";
import type { Statistics } from "../../../../../api/infrastructure/statistics.formatter";
import NoDataMessage from "../../../utils/NoDataMessage/NoDataMessage";
import "./StatisticsPart.scss";

const storedColors: Map<string, string> = new Map();

export default function StatisticsPart({
  statistics,
  defaultReferenceId,
  defaultTableId,
}: {
  defaultReferenceId: number;
  defaultTableId: number;
  statistics: Statistics | RequestError;
}) {
  const wrapAsync = useWrapAsync();
  const [generatedReference, setGeneratedReference] = useState<GeneratedReference>();
  const [chartData, setChartData] = useState<ChartData>();

  useEffect(() => {
    if (checkIsRequestError(statistics) || !statistics.diagramData) return;
    const labels: string[] = [];
    const diagramData: number[] = [];
    const diagramColors: string[] = [];
    for (const [key, val] of statistics.diagramData) {
      labels.push(key);
      diagramData.push(val);
      const colorKey = key.toLowerCase().trim().replace(/\s+/g, "-");
      const storedColor = storedColors.get(colorKey);
      let color: string;
      if (storedColor) color = storedColor;
      else {
        color = generateHexCodeColor();
        storedColors.set(colorKey, color);
      }
      diagramColors.push(color);
    }
    const data: ChartData = {
      labels,
      datasets: [
        {
          data: diagramData,
          backgroundColor: diagramColors,
          spacing: 20,
          hoverOffset: 10,
        },
      ],
    };

    setChartData(data);
  }, [statistics]);
  useEffect(
    wrapAsync(async () => {
      setGeneratedReference(undefined);
      await apiRequestWrapper(
        async () => {
          setGeneratedReference(await fetchOneGeneratedReference(defaultReferenceId));
        },
        { handle404: true },
      );
    }),
    [defaultReferenceId],
  );

  return (
    <div className="StatisticsPart">
      {checkIsRequestError(statistics) ? (
        <div className="Card StatisticsLoadFailed warn onCardList">
          {statistics.error ? statistics.error : "Le chargement des statistiques a échoué"}
        </div>
      ) : (
        <div className="StatisticsPart-cardList">
          {statistics.kpis.map((s) => {
            const id = `${s.property}${getSeq()}`;
            return (
              <StatsCard
                id={id}
                key={`card-${id}`}
                label={s.name}
                total={s.data.count}
                date={s.data.updatedAt}
              />
            );
          })}
        </div>
      )}

      <div className="StatisticsPart-chartAndDatatable">
        <ChartPart statistics={statistics} chartData={chartData} />
        {generatedReference ? (
          <ReferenceDataDataTable
            canMutate={false}
            defaultGeneratedReferenceId={generatedReference.id}
            defaultTableId={defaultTableId}
            footer
          />
        ) : (
          <NoDataMessage message="Référentiel non trouvé" />
        )}
      </div>
    </div>
  );
}
